import type { Subscription } from './types';
import { useI18nStore, useT } from '@/shared/lib/i18n';

interface Props {
  sub: Subscription;
  memberCount: number;
}

const CURRENCY_SYMBOL: Record<string, string> = { RUB: '₽', USD: '$', EUR: '€' };

function periodLabel(period: { interval: number; unit: string }, t: ReturnType<typeof useT>): string {
  if (period.unit === 'months') {
    if (period.interval === 1) return t('subscription.periodMonthly');
    if (period.interval === 3) return t('subscription.periodQuarterly');
    if (period.interval === 12) return t('subscription.periodYearly');
    return t('common.periodEveryMonthsShort', { count: period.interval });
  }
  if (period.unit === 'weeks') return t('common.periodEveryWeeksShort', { count: period.interval });
  return t('common.periodEveryDaysShort', { count: period.interval });
}

function formatDate(dateStr: string | null, lang: 'ru' | 'en', t: ReturnType<typeof useT>): string {
  if (!dateStr) return t('common.dash');
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString(lang, { day: 'numeric', month: 'long' });
}

export function HolderSummaryCard({ sub, memberCount }: Props) {
  const t = useT();
  const lang = useI18nStore((s) => s.lang);
  const sym = CURRENCY_SYMBOL[sub.currency] ?? sub.currency;

  return (
    <div className="rounded-[22px] p-[18px] bg-[rgba(240,250,242,0.34)] border border-white/62 shadow-[0_18px_48px_rgba(18,52,36,0.13),inset_0_1px_0_rgba(255,255,255,0.42)] backdrop-blur-[11px]">
      {/* Top row */}
      <div className="flex justify-between items-start">
        <div>
          <p className="text-[13px] text-text-secondary">
            {periodLabel(sub.period, t)} · {formatDate(sub.billing_date, lang, t)}
          </p>
          <p className="text-[26px] font-bold font-mono tabular-nums mt-0.5">
            {Number(sub.total_price).toLocaleString('ru')} {sym}
          </p>
        </div>
        <div className="text-right text-[12px] text-text-secondary leading-relaxed">
          <p>{t('subscription.membersCountShort', { count: memberCount })}</p>
        </div>
      </div>

      {/* Billing date row */}
      {sub.billing_date && (
        <p className="mt-2 text-[12px] text-text-secondary">
          {t('subscription.billingDate', { date: formatDate(sub.billing_date, lang, t) })}
        </p>
      )}
    </div>
  );
}
